import type { Theme } from '@mui/material'
import type { SyntheticEvent } from 'react'
import type { DayKey, Organizer, PublicJudge, RegistrationClass } from '../../types'
import type { DayValue } from '../components/DateRange'
import type { FilterProps } from '../state'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { useMediaQuery } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FOCUS_RING, HEADER_HEIGHT } from '../../assets/Theme'
import { addMonths, dayOf, TIME_ZONE } from '../../lib/day'
import AutocompleteMulti from '../components/AutocompleteMulti'
import DateRange from '../components/DateRange'
import SelectMulti from '../components/SelectMulti'
import { filterStrings } from '../state'

interface Props {
  readonly eventCount: number
  readonly eventTypes: string[]
  readonly eventClasses: RegistrationClass[]
  readonly filter: FilterProps
  readonly judges: PublicJudge[]
  readonly onChange?: (filter: FilterProps) => void
  readonly organizers: Organizer[]
}

/** The first day the range offers. A key: the browser's midnight of 1.1.2020 is 31.12.2019 in Finland east of it (KOE-1548). */
const MIN_DAY: DayKey = '2020-01-01'
const DATE_RANGE = { start: MIN_DAY }

export const EventFilter = ({ judges, organizers, eventTypes, eventClasses, filter, onChange, eventCount }: Props) => {
  const { t } = useTranslation()
  const md = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'))
  const [expanded, setExpanded] = useState(md)
  const filters = useMemo(() => filterStrings(filter, t), [filter, t])
  const resultsLabel = t('filter.results', { count: eventCount })
  const titleLabel = t('filter.title')

  const setFilter = useCallback(
    (props: Partial<FilterProps>) => onChange?.({ ...filter, ...props }),
    [filter, onChange]
  )
  // The range gives whole days, as the URL holds them (KOE-1538, KOE-1548).
  const handleDateRangeChange = useCallback((start: DayValue, end: DayValue) => setFilter({ end, start }), [setFilter])
  const handleEventTypeChange = useCallback(
    (value: readonly string[]) => setFilter({ eventType: [...value] }),
    [setFilter]
  )
  const handleEventClassChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: readonly RegistrationClass[]) =>
      setFilter({ eventClass: [...value] }),
    [setFilter]
  )
  const handleOrganizerChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: readonly Organizer[]) =>
      setFilter({ organizer: value.map((v) => v.id) }),
    [setFilter]
  )
  const handleJudgeChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: readonly PublicJudge[]) =>
      setFilter({ judge: value.map((v) => v.name) }),
    [setFilter]
  )
  const handleWithEntryOpenChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, checked: boolean) =>
      setFilter({
        withClosingEntry: checked && filter.withClosingEntry,
        withFreePlaces: checked && filter.withFreePlaces,
        withOpenEntry: checked,
      }),
    [filter.withClosingEntry, filter.withFreePlaces, setFilter]
  )
  const handleWithUpcomingEntryChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, checked: boolean) =>
      setFilter({
        withUpcomingEntry: checked,
      }),
    [setFilter]
  )
  const handleWithResultsChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, checked: boolean) => {
      // Results exist only on ended events and the default range starts today, so switching this on
      // with the default range would show nothing. Widen backwards; the moved date stays visible in
      // the range field, so the reader sees why past events appeared and can adjust it.
      const todayKey = dayOf(new Date(), TIME_ZONE)
      const widen = checked && filter.start && filter.start >= todayKey

      setFilter({
        withResults: checked,
        ...(widen ? { start: addMonths(todayKey, -6) } : {}),
      })
    },
    [filter.start, setFilter]
  )
  const getName = useCallback((o?: { name?: string }) => o?.name ?? '', [])
  const getJudgeName = useCallback(
    (o?: PublicJudge) => (o?.name ?? '') + (o?.foreing && o.country ? ` (${t(o.country, { ns: 'country' })})` : ''),
    [t]
  )
  const getString = useCallback((o?: string) => o ?? '', [])
  const compareId = useCallback((o?: { id?: number | string }, v?: { id?: number | string }) => o?.id === v?.id, [])
  const compareJudge = useCallback((o?: PublicJudge, v?: PublicJudge) => o?.name === v?.name, [])

  return (
    <Box
      component="nav"
      sx={{
        border: '1px solid #708f85',
        borderRadius: '4px',
        m: '1px',
        p: 0,
        position: 'sticky',
        top: `calc(${HEADER_HEIGHT} - 1px)`,

        // borderTop: '1px solid #708f85',
        zIndex: 2,
      }}
    >
      <Accordion
        defaultExpanded={md}
        expanded={expanded}
        onChange={(_e, expanded) => setExpanded(expanded)}
        // MUI heads the summary with an h3; under the page's h1 that skips a level (KOE-1562).
        slotProps={{ heading: { component: 'h2' } }}
        sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
      >
        <AccordionSummary
          sx={{
            '@media (hover: hover)': { '&:hover .filter-header': { boxShadow: 4 } },
            '& .MuiAccordionSummary-content': { margin: 0, overflow: 'hidden' },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
            '&.Mui-expanded': { minHeight: '28px' },
            // The header is the summary's only content and covers the tint MUI marks focus with, so
            // keyboard focus gets the ring the theme gives a button. Drawn inside: outside it would
            // sit on the filter's border and slide under the app bar once the filter sticks.
            '&.Mui-focusVisible': { outline: FOCUS_RING, outlineOffset: -2 },
            bgcolor: 'background.filterHeader',
            minHeight: '28px',
            p: '1px',
          }}
        >
          {/* Drawn like a contained button, but not one: the summary is the button, and a focusable
              control inside it would be a second tab stop and a button inside a button (KOE-1560). */}
          <Box
            component="span"
            className="filter-header"
            sx={(theme) => ({
              ...theme.typography.button,
              alignItems: 'center',
              bgcolor: 'background.filterHeader',
              borderRadius: 1,
              boxShadow: 2,
              boxSizing: 'border-box',
              color: 'black',
              display: 'flex',
              justifyContent: 'center',
              // A button is one line of its own text tall, whatever the smaller caption inside needs.
              minHeight: 'calc(1lh + 12px)',
              minWidth: 64,
              px: 2,
              py: '6px',
              transition: theme.transitions.create('box-shadow', { duration: theme.transitions.duration.short }),
              width: '100%',
            })}
          >
            <TuneOutlinedIcon sx={{ fontSize: 20, ml: '-4px', mr: 1 }} />
            <Typography
              variant="caption"
              noWrap
              sx={{
                textOverflow: 'ellipsis',
              }}
            >
              <b>
                {titleLabel} ({filters.length})
              </b>{' '}
              • {filters.join(' | ')} • {resultsLabel}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: 'background.filter' }}>
          <Grid
            container
            spacing={1}
            sx={{
              justifyContent: 'start',
            }}
          >
            <Grid size={{ md: 6, xs: 12 }}>
              <DateRange
                start={filter.start}
                end={filter.end}
                range={DATE_RANGE}
                startLabel={t('daterangeStart')}
                endLabel={t('daterangeEnd')}
                onChange={handleDateRangeChange}
              />
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <SelectMulti
                label={t('filter.eventType')}
                onChange={handleEventTypeChange}
                options={eventTypes}
                value={filter.eventType}
              />
            </Grid>
            <Grid size={{ md: 2, sm: 6, xs: 12 }}>
              <AutocompleteMulti
                getOptionLabel={getString}
                label={t('filter.eventClass')}
                onChange={handleEventClassChange}
                options={eventClasses}
                value={filter.eventClass}
              />
            </Grid>
            <Grid size={{ sm: 6, xs: 12 }}>
              <AutocompleteMulti
                getOptionLabel={getName}
                isOptionEqualToValue={compareId}
                label={t('filter.organizer')}
                onChange={handleOrganizerChange}
                options={organizers}
                value={organizers.filter((o) => filter.organizer.includes(o.id))}
              />
            </Grid>
            <Grid size={{ sm: 6, xs: 12 }}>
              <AutocompleteMulti
                getOptionLabel={getJudgeName}
                isOptionEqualToValue={compareJudge}
                label={t('judge')}
                onChange={handleJudgeChange}
                options={judges}
                value={judges.filter((j) => filter.judge.includes(j.name))}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Grid container>
                <Grid size="auto">
                  <Stack
                    direction={{ sm: 'row', xs: 'column' }}
                    spacing={0}
                    sx={{
                      alignItems: 'start',
                      justifyContent: 'start',
                    }}
                  >
                    <FormControlLabel
                      value="withOpenEntry"
                      checked={filter.withOpenEntry}
                      control={<Switch />}
                      label={t('entryOpen')}
                      name="withOpenEntry"
                      onChange={handleWithEntryOpenChange}
                    />
                    <FormControlLabel
                      value="withUpcomingEntry"
                      checked={filter.withUpcomingEntry}
                      control={<Switch />}
                      label={t('entryUpcoming')}
                      labelPlacement="end"
                      name="withUpcomingEntry"
                      onChange={handleWithUpcomingEntryChange}
                    />
                    <FormControlLabel
                      value="withResults"
                      checked={filter.withResults ?? false}
                      control={<Switch />}
                      label={t('filter.resultsPublished')}
                      labelPlacement="end"
                      name="withResults"
                      onChange={handleWithResultsChange}
                    />
                  </Stack>
                </Grid>
                <Grid
                  sx={{
                    alignSelf: 'end',
                    display: { md: 'none', xs: undefined },
                  }}
                >
                  <Button variant="contained" onClick={() => setExpanded(false)}>
                    OK
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
